import DOMPurify from 'isomorphic-dompurify';

// Shared allowlist for authored rich text — matches what the lesson
// RichTextEditor can produce (bold, italic, headings, lists, quotes,
// links, highlights) so every "description" field across the site
// (events, opportunities, sessions, community posts) renders with the
// SAME styling and safety guarantees as a lesson's Reading block.
const ALLOWED_TAGS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'del',
  'p', 'br',
  'ul', 'ol', 'li',
  'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'span', 'code', 'pre', 'blockquote',
  'mark', 'hr',
  'div',
];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style'];

// Detects any HTML-like markup so plain text with newlines still
// gets a sensible whitespace-preserving fallback.
const looksLikeHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);

// Strip WYSIWYG whitespace pollution that inflates block spacing so LEGACY
// content saved before the editor was tightened still matches the
// `.prose-reading` rhythm used in the learning module Reading block.
// (New content is already normalized in `RichTextEditor.emit()`.)
const normalizeWhitespace = (html) => (html || '')
  .replace(/<p[^>]*>\s*(<br\s*\/?>|&nbsp;|\u00a0)?\s*<\/p>/gi, '')
  .replace(/<div[^>]*>\s*(<br\s*\/?>|&nbsp;|\u00a0)?\s*<\/div>/gi, '')
  .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');

const sanitize = (html) => DOMPurify.sanitize(normalizeWhitespace(html), { ALLOWED_TAGS, ALLOWED_ATTR });

// Plain text saved with HTML entities (e.g. "beauty &amp; care" pasted from a
// rich editor) must render the real characters. DOMParser builds an inert
// document — entities decode, nothing executes.
const decodeEntities = (s) => {
  const str = String(s);
  if (!/&[a-zA-Z#0-9]+;/.test(str)) return str;
  try {
    return new DOMParser().parseFromString(str, 'text/html').body.textContent || str;
  } catch {
    return str;
  }
};

// Strip tags + decode entities — for one-line card previews of rich fields.
export const stripRichText = (s) => {
  if (!s) return '';
  try {
    return (new DOMParser().parseFromString(String(s), 'text/html').body.textContent || '')
      .replace(/\s+/g, ' ').trim();
  } catch {
    return String(s).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
};

/**
 * RichHtml — renders authored rich-text safely with the site-wide
 * `.prose-reading` typography (defined in App.css). Falls back to a
 * `whitespace-pre-line` <p> when the value is plain text so old
 * newline-only descriptions keep their line breaks.
 *
 * Use this instead of `dangerouslySetInnerHTML + prose prose-*` — the
 * Tailwind Typography plugin is intentionally not installed, so those
 * classes render as unstyled bare HTML on the web.
 */
const RichHtml = ({ value, className = '', testid }) => {
  if (!value) return null;
  const html = looksLikeHtml(value);
  if (html) {
    return (
      <div
        className={`prose-reading text-sm sm:text-base text-foreground/85 leading-relaxed ${className}`}
        data-testid={testid}
        dangerouslySetInnerHTML={{ __html: sanitize(value) }}
      />
    );
  }
  return (
    <p
      className={`prose-reading text-sm sm:text-base text-foreground/85 leading-relaxed whitespace-pre-line ${className}`}
      data-testid={testid}
    >
      {decodeEntities(value)}
    </p>
  );
};

export default RichHtml;
